/**
 * The small arithmetic the Kivi model runs on, ported from the Kivi guide
 * (design/explorations/kivi/kivi-in-recordos.html). Every function is a
 * worklet, so the phone can run it on the UI thread.
 */
import type { KiviCubic, KiviMatrix, KiviPoint } from './types';

export function lerp(a: number, b: number, t: number): number {
  'worklet';
  return a + (b - a) * t;
}

export function clamp(x: number, a = 0, b = 1): number {
  'worklet';
  return Math.min(b, Math.max(a, x));
}

/** The page's number format: two decimals, no trailing zeros. */
export function f2(n: number): string {
  'worklet';
  return (Math.round(n * 100) / 100).toString();
}

/** A point on a cubic at t. */
export function bez(P: KiviCubic, t: number): KiviPoint {
  'worklet';
  const u = 1 - t;
  return [
    u * u * u * P[0][0] + 3 * u * u * t * P[1][0] + 3 * u * t * t * P[2][0] + t * t * t * P[3][0],
    u * u * u * P[0][1] + 3 * u * u * t * P[1][1] + 3 * u * t * t * P[2][1] + t * t * t * P[3][1],
  ];
}

export function easeOut(t: number): number {
  'worklet';
  const c = clamp(t);
  return 1 - Math.pow(1 - c, 3);
}

export function easeIO(t: number): number {
  'worklet';
  const c = clamp(t);
  return c < 0.5 ? 4 * c * c * c : 1 - Math.pow(-2 * c + 2, 3) / 2;
}

/** A triangle wave, -1..1. */
export function tri(z: number): number {
  'worklet';
  const w = ((z % 1) + 1) % 1;
  return Math.abs(w * 4 - 2) - 1;
}

export const KIVI_IDENTITY: KiviMatrix = [1, 0, 0, 1, 0, 0];

/** m · n: n applied first. */
export function matMul(m: KiviMatrix, n: KiviMatrix): KiviMatrix {
  'worklet';
  return [
    m[0] * n[0] + m[2] * n[1],
    m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3],
    m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4],
    m[1] * n[4] + m[3] * n[5] + m[5],
  ];
}

export function matTranslate(x: number, y: number): KiviMatrix {
  'worklet';
  return [1, 0, 0, 1, x, y];
}

export function matScale(x: number, y: number): KiviMatrix {
  'worklet';
  return [x, 0, 0, y, 0, 0];
}

/** SVG's rotate(deg cx cy). */
export function matRotate(deg: number, cx: number, cy: number): KiviMatrix {
  'worklet';
  const a = (deg * Math.PI) / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  return [c, s, -s, c, cx - c * cx + s * cy, cy - s * cx - c * cy];
}

/** The page's mixc: a colour t of the way from a to b, both #RRGGBB, as #RRGGBB. */
export function kiviMixHex(a: string, b: string, t: number): string {
  'worklet';
  let out = '#';
  for (let i = 1; i <= 5; i += 2) {
    const v = Math.round(lerp(parseInt(a.slice(i, i + 2), 16), parseInt(b.slice(i, i + 2), 16), t));
    out += v.toString(16).padStart(2, '0');
  }
  return out.toUpperCase();
}
